import { useState } from "react";
import { API } from "@/lib/api";
import { Sparkles, X, Loader2 } from "lucide-react";

export default function AIDrawer({ reportKey, reportTitle, buttonLabel = "AI Insights" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setText("");
    setError("");
    try {
      const res = await fetch(`${API}/insights/${reportKey}`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(`Insight request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const j = JSON.parse(p.slice(6));
          if (j.delta) setText((t) => t + j.delta);
          if (j.error) setError(j.error);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = () => {
    setOpen(true);
    if (!text && !loading) run();
  };

  return (
    <>
      <button
        onClick={openDrawer}
        data-testid={`${reportKey}-ai-insights-button`}
        className="flex items-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] text-white text-sm font-medium rounded-lg px-4 py-2 shadow-sm shadow-red-900/20 transition-colors"
      >
        <Sparkles size={15} /> {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg h-full bg-white border-l border-slate-200 flex flex-col fade-up" data-testid="ai-insights-drawer">
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-200">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md bg-[#B01E24]/10 text-[#B01E24]"><Sparkles size={16} /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">AI Insights</div>
                  <div className="text-[11px] text-slate-500">{reportTitle} · GPT-5.4</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-drawer-close-button" className="p-2 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto thin-scroll p-5">
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-3">{error}</div>}
              {loading && !text && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 size={15} className="animate-spin" /> Analysing {reportTitle}…
                </div>
              )}
              <div className="text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-wrap" data-testid="ai-insights-content">{text}</div>
              {loading && text && <span className="inline-block w-2 h-4 bg-[#B01E24] animate-pulse ml-0.5 align-text-bottom" />}
            </div>
            <div className="p-4 border-t border-slate-200">
              <button
                onClick={run}
                disabled={loading}
                data-testid="ai-regenerate-button"
                className="w-full flex items-center justify-center gap-2 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 border border-slate-300 rounded-lg px-4 py-2 text-sm transition-colors"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Regenerate Analysis
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
